/**
 * Utility functions for Access Order security logic
 * These functions help prepare user information for category filtering
 */

/**
 * Extracts the user's access level from employee_permission details
 * @param {Array} employeePermissions - Array of employee permissions from state.employee_permission.employee_permission
 * @returns {number} The access level (defaults to 0 if not found)
 */
export const getAccessLevelFromPermissions = (employeePermissions) => {
  if (!employeePermissions || !Array.isArray(employeePermissions)) {
    return 0;
  }

  // Look through permission records to find special_permission
  for (const permission of employeePermissions) {
    // Check both auth_details and details arrays for special_permission
    const detailsArrays = [
      permission.auth_details,
      permission.details,
      permission.updated?.auth_details,
      permission.updated?.details,
      permission.created?.auth_details,
      permission.created?.details,
    ].filter(Boolean);

    for (const detailsArray of detailsArrays) {
      if (Array.isArray(detailsArray)) {
        const specialPermission = detailsArray.find(
          (detail) => 
            detail?.name_id === "special_permission" ||
            detail?.name === "special_permission" ||
            detail?.value === "special_permission"
        );

        if (specialPermission) {
          // Return the response or value as the access level
          const accessLevel =
            specialPermission.response !== undefined
              ? specialPermission.response
              : specialPermission.value;

          return parseInt(accessLevel, 10) || 0;
        }
      }
    }
  }

  return 0;
};

/**
 * Prepares user info object for SET_CORE_DATA action
 * @param {number} accountType - User's account type from auth
 * @param {Array} employeePermissions - Employee permissions array
 * @returns {Object} User info object with account_type and access_level
 */
export const prepareUserInfoForCoreData = (accountType, employeePermissions) => {
  return {
    account_type: accountType,
    access_level: getAccessLevelFromPermissions(employeePermissions),
  };
};

/**
 * Filters categories based on Access Order security logic
 * This is a standalone version that can be used outside the reducer if needed
 * @param {Array} categories - The categories array to filter
 * @param {number} accountType - User's account type
 * @param {number} accessLevel - User's access level from special_permission
 * @returns {Array} Filtered categories
 */
export const filterCategoriesByAccessOrder = (
  categories,
  accountType,
  accessLevel
) => {
  // Admin users (account_type === 5) see all categories
  if (accountType === 5) {
    return categories;
  }

  // For non-admins, filter based on require_special_access and access_order
  return categories.filter((category) => {
    // If require_special_access is false, always include the category
    if (!category.require_special_access) {
      return true;
    }

    // If require_special_access is true, check access_order against access_level
    // Only include if access_order <= access_level
    return category.access_order <= accessLevel;
  });
};

